import type { AggregatedActivityResponse } from 'api/__GENERATED';
import * as R from 'remeda';
import { apiUtility } from 'utility';

// Maps a single AggregatedActivity DTO (returned at `Employee` resolution)
// onto the normalized `Data.AggregatedEmployeeHour` shape.
//
// At Employee resolution the server fans rows out per (employee × activity ×
// date) so `employeeId`, `activityId`, `activityTypeId`, and at least `year`
// are required for the row to be addressable. `dayOfWeek` is always null on
// this call (the spec emits day-of-month only) so it's mapped through as
// undefined rather than treated as missing.
//
// `monthNumber` is 1-based on the wire and shifted to 0-based `month` to match
// the rest of our `Time` types. `id` is generated locally — the endpoint
// returns no stable server-side identity for aggregated rows. Returning
// `undefined` lets the domain transformer filter unaddressable rows out.
function toEmployeeHour(
  dto: AggregatedActivityResponse,
): Data.AggregatedEmployeeHour | undefined {
  const {
    activityId,
    activityTypeId,
    employeeId,
    year,
    monthNumber,
    weekNumber,
    dayOfMonth,
    dayOfWeek,
    spentTime,
  } = dto;

  if (
    !R.isDefined(activityId) ||
    !R.isDefined(activityTypeId) ||
    !R.isDefined(employeeId) ||
    !R.isDefined(year)
  ) {
    return undefined;
  }

  const dateId = apiUtility.extractDateId({
    dayOfMonth,
    monthNumber,
    weekNumber,
    year,
  });

  if (dateId === '') return undefined;

  const employeeHour: Data.AggregatedEmployeeHour = {
    id: R.randomString(12),
    dateId,
    activityId,
    activityTypeId,
    employeeId,
    spentTime,
    year,
    dayOfMonth: R.isDefined(dayOfMonth) ? dayOfMonth : undefined,
    dayOfWeek: R.isDefined(dayOfWeek) ? dayOfWeek : undefined,
    month: R.isDefined(monthNumber) ? monthNumber - 1 : undefined,
    week: R.isDefined(weekNumber) ? weekNumber : undefined,
  };

  return employeeHour;
}

export { toEmployeeHour };
